import { FC } from 'react'

import { useAutos } from '@/api/models/parking'
import { useInviteDetails } from '@/api/models/users/hooks/useInviteDetails'
import { UserAutosHeaderSkeleton } from '@/components/entities/user/ui/UserAuto/ui/UserAutosSkeleton'
import { UserAutoWrapper } from '@/components/entities/user/ui/UserAuto/ui/UserAutoWrapper'
import { AppDrawer, useDrawerRectObserver } from '@/components/shared/drawer'
import { MODAL_TITLE_ERROR } from '@/utils/constants'

import {
  CloseUserProfile,
  SetUserProfileRoute,
  UserProfileRoute
} from '../hooks/useUserProfileRoute'
import { UserProfileScreens } from '../lib/types'
import { UserContactScreen } from './UserContactScreen'
import { useUserContactsStore } from './UserContactsScreen/model/store'
import UserContactsDividingScreen from './UserContactsScreen/ui/UserContactsDividingScreen'
import { UserDocumentsScreen } from './UserDocumentsScreen'
import { UserInviteScreen } from './UserInviteScreen'
import { InviteCreationScreen } from './UserInviteScreen/ui/InviteCreationScreen'
import { UserInviteSkeleton } from './UserInviteScreen/ui/UserInviteSkeleton'
import UserProfileHeader from './UserProfileHeader'
import UserProfileScreen from './UserProfileScreen'
import { UserProjectsScreen } from './UserProjectsScreen'

type Props = {
  opened: boolean
  userProfileRoute: UserProfileRoute | null
  setUserProfileRoute: SetUserProfileRoute
  closeUserProfile: CloseUserProfile
}

const UserProfileDrawer: FC<Props> = ({
  opened,
  userProfileRoute,
  setUserProfileRoute,
  closeUserProfile
}) => {
  const { setHeaderRef, setFooterRef, contentHeight } = useDrawerRectObserver()
  const { setSelectedProjects } = useUserContactsStore()
  const isAutosScreen = [
    UserProfileScreens.Autos,
    UserProfileScreens.Auto
  ].includes(userProfileRoute?.screen as UserProfileScreens)

  const { isLoading: areAutosLoading, error: autosError } = useAutos({
    enabled: isAutosScreen
  })
  const isAutosSkeleton = isAutosScreen && areAutosLoading
  const isAutosError = isAutosScreen && Boolean(autosError)
  const {
    data: invite,
    isFetching,
    isError
  } = useInviteDetails({
    id: userProfileRoute?.entityId ?? '',
    enabled: userProfileRoute?.screen === UserProfileScreens.Invite
  })

  const onClose = () => {
    setSelectedProjects(undefined)
    closeUserProfile()
  }

  const renderTitle = () => {
    if (isAutosSkeleton) {
      return <UserAutosHeaderSkeleton />
    }

    if (isAutosError) {
      return <h3>{MODAL_TITLE_ERROR}</h3>
    }

    return (
      <UserProfileHeader
        userProfileScreen={userProfileRoute?.screen ?? ''}
        setUserProfileRoute={setUserProfileRoute}
        invite={
          userProfileRoute?.screen === UserProfileScreens.Invite
            ? invite
            : undefined
        }
      />
    )
  }

  return (
    <AppDrawer
      opened={opened}
      ref={setHeaderRef}
      isLoading={isAutosSkeleton}
      title={renderTitle()}
      className="w-[658px]"
      contentWrapperClassName="!px-0"
      onClose={onClose}
    >
      {userProfileRoute?.screen === UserProfileScreens.Profile && (
        <UserProfileScreen
          setUserProfileRoute={setUserProfileRoute}
          contentHeight={contentHeight}
          setFooterRef={setFooterRef}
        />
      )}
      {(userProfileRoute?.screen === UserProfileScreens.Accesses ||
        userProfileRoute?.screen === UserProfileScreens.Contacts) && (
        <UserContactsDividingScreen
          setUserProfileRoute={setUserProfileRoute}
          userProfileRoute={userProfileRoute}
        />
      )}
      {userProfileRoute?.screen === UserProfileScreens.Contact && (
        <UserContactScreen
          contactId={userProfileRoute.entityId ?? ''}
          setUserProfileRoute={setUserProfileRoute}
          contentHeight={contentHeight}
          setFooterRef={setFooterRef}
        />
      )}
      {userProfileRoute?.screen === UserProfileScreens.Invite &&
        invite &&
        !isError && (
          <UserInviteScreen
            invite={invite}
            setUserProfileRoute={setUserProfileRoute}
          />
        )}
      {userProfileRoute?.screen === UserProfileScreens.Invite &&
        isFetching &&
        !invite && <UserInviteSkeleton />}

      {userProfileRoute?.screen === UserProfileScreens.InviteCreation && (
        <InviteCreationScreen setUserProfileRoute={setUserProfileRoute} />
      )}
      {userProfileRoute?.screen === UserProfileScreens.Projects && (
        <UserProjectsScreen />
      )}
      {isAutosScreen && (
        <UserAutoWrapper
          onClose={onClose}
          userProfileRoute={userProfileRoute!}
          setUserProfileRoute={setUserProfileRoute}
        />
      )}

      {userProfileRoute?.screen === UserProfileScreens.Documents && (
        <UserDocumentsScreen />
      )}
    </AppDrawer>
  )
}

export default UserProfileDrawer
